import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Spin, Card, Descriptions, Select, Button,
  Typography,  Row, Col, Tag, Modal,
} from 'antd';
import useGetQueueDetail from './hooks/useGetQueueDetail';
import useGetStatusOptions from './hooks/useGetStatusOptions';
import useGetAllActiveQueue from '@Hooks/queries/useGetAllActiveQueue';
import PassedQueueHistory from './components/PassedQueueHistory';
import useUpdateQueueStatus from './hooks/useUpdateQueueStatus';
import './DetailActiveQueue.css';

const { Text } = Typography;

const DetailActiveQueue = () => {
  // Route: /active-queue/:value/:shiftId (App.js)
  const { value: queueId, shiftId } = useParams();

  // Reference for layout measurement
  const containerRef = useRef(null);
  const [footerLeft, setFooterLeft] = useState(250);

  useEffect(() => {
    const updateLayout = () => {
      if (containerRef.current) {
        setFooterLeft(containerRef.current.getBoundingClientRect().left);
      }
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    
    let observer;
    if (window.ResizeObserver && containerRef.current) {
      observer = new ResizeObserver(updateLayout);
      observer.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener('resize', updateLayout);
      if (observer) observer.disconnect();
    };
  }, []);

  // selectedStatus menyimpan integer ID status (bukan objek)
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isNoteOpen,     setIsNoteOpen]     = useState(false);
  const [isVaccinOpen,   setIsVaccinOpen]   = useState(false);

  // Hook 1: fetch + transform data detail antrian
  const {
    isLoading,
    childData,
    visitData,
    vaccinationData,
    currentStatus,
    passedHistory,
  } = useGetQueueDetail(Number(queueId));

  // Hook 2: fetch + transform opsi status dropdown
  const { statusOptions, isLoading: isStatusLoading } = useGetStatusOptions();

  // Hook 3: update status antrian + notifikasi + navigasi
  const { updateStatus, isLoading: isUpdating } = useUpdateQueueStatus(
    Number(queueId),
    Number(shiftId)
  );

  // Sync selectedStatus ketika data pertama kali dimuat (simpan hanya integer ID)
  useEffect(() => {
    if (currentStatus) setSelectedStatus(currentStatus.value);
  }, [currentStatus]);

  // Info antrian terlewat untuk antrian ini (hanya ada selama statusnya Passed)
  const allActiveQueue = useGetAllActiveQueue();
  const passedInfo = allActiveQueue?.data?.passed_queues?.find(
    (item) => item?.queue_id === Number(queueId)
  );
  const passedThreshold = allActiveQueue?.data?.passed_queue_threshold;

  const isPageLoading = isLoading || isStatusLoading || isUpdating;

  return (
    <Spin spinning={isPageLoading} tip="Memuat data..." size="large">
      <div className="content-section" ref={containerRef}>
        <div className="title-section mb-5">
          <h1 className="user-page-title">Detail Anak</h1>
          <h2 className="user-page-subtitle">
            <span style={{ color: '#039BEB' }}>Antrian</span>
            <span style={{ color: '#039BEB' }}>/ Daftar Antrian</span>
            <span style={{ color: '#00000029' }}> / Detail Anak</span>
          </h2>
        </div>

        <Card className="shadow mb-5" bordered={false} style={{ textAlign: 'left' }}>

          {/* Badge pekerja kesehatan */}
          {childData?.isHealthWorker === 1 && (
            <div style={{ marginBottom: 16 }}>
              <Tag color="blue">Orang Tua Pekerja Kesehatan</Tag>
              <Tag color="green" style={{ marginLeft: 8 }}>Bisa Digratiskan</Tag>
            </div>
          )}

          <Row gutter={[24, 24]}>
            {/* Kolom 1: Orang Tua */}
            <Col xs={24} lg={8}>
              <Descriptions
                title="Orang Tua"
                bordered
                column={1}
                size="small"
                className="mb-4"
                labelStyle={{ width: '40%' }}
              >
                <Descriptions.Item label="Nama Ayah">{childData?.fatherName || '-'}</Descriptions.Item>
                <Descriptions.Item label="Nama Ibu">{childData?.motherName || '-'}</Descriptions.Item>
                <Descriptions.Item label="Alamat">{childData?.address || '-'}</Descriptions.Item>
                <Descriptions.Item label="No. Telp Ayah">{childData?.fatherPhone || '-'}</Descriptions.Item>
                <Descriptions.Item label="No. Telp Ibu">{childData?.motherPhone || '-'}</Descriptions.Item>
              </Descriptions>
            </Col>

            {/* Kolom 2: Anak */}
            <Col xs={24} lg={8}>
              <Descriptions
                title="Anak"
                bordered
                column={1}
                size="small"
                className="mb-4"
                labelStyle={{ width: '40%' }}
              >
                <Descriptions.Item label="Nama">{childData?.name || '-'}</Descriptions.Item>
                <Descriptions.Item label="Tanggal Lahir">{childData?.birthdate ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="Jenis Kelamin">
                  {childData?.gender === 'male' ? 'Laki-laki' : 'Perempuan'}
                </Descriptions.Item>
              </Descriptions>
            </Col>

            {/* Kolom 3: Waktu */}
            <Col xs={24} lg={8}>
              <Descriptions
                title="Waktu"
                bordered
                column={1}
                size="small"
                className="mb-4"
                labelStyle={{ width: '40%' }}
              >
                <Descriptions.Item label="Kunjungan">{visitData?.shift || '-'}</Descriptions.Item>
                <Descriptions.Item label="Tanggal">{visitData?.date || '-'}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>

          {(passedInfo || passedHistory.finishedSincePassed.length > 0) && (
            <PassedQueueHistory
              title={
                passedInfo
                  ? 'Informasi Antrian Terlewat'
                  : 'Riwayat Antrian Terlewat'
              }
              passedAt={passedInfo?.passed_at}
              queuesPassedCount={passedInfo?.queues_passed_count}
              threshold={passedThreshold}
              finishedSincePassed={
                passedInfo?.finished_since_passed ??
                passedHistory.finishedSincePassed
              }
              isStillPassed={!!passedInfo}
            />
          )}

          {passedHistory.recreatedFrom && (
            <PassedQueueHistory
              title={`Tiket ini dibuat ulang dari antrian No. ${
                passedHistory.recreatedFrom.queue_number ?? '-'
              } (${passedHistory.recreatedFrom.child_name || '-'}) yang dibatalkan otomatis`}
              passedAt={passedHistory.recreatedFrom.passed_at}
              queuesPassedCount={passedHistory.recreatedFrom.queues_passed_count}
              threshold={passedThreshold}
              finishedSincePassed={
                passedHistory.recreatedFrom.finished_since_passed
              }
            />
          )}

          <Row gutter={[24, 24]}>
            {/* Full width: Informasi Vaksin */}
            <Col xs={24}>
              <div className="mb-4">
                <div className="ant-descriptions-header">
                  <div className="ant-descriptions-title">Informasi Vaksin</div>
                </div>
                {vaccinationData?.length > 0 ? (
                  vaccinationData.map((item, index) => (
                    <Card
                      type="inner"
                      title={`Vaksin ${index + 1}`}
                      key={index}
                      className="mb-3"
                      size="small"
                    >
                      <Descriptions bordered column={1} size="small" className="mb-3" labelStyle={{ width: '20%' }}>
                        <Descriptions.Item label="Merek Vaksin">{item?.vaccine?.brand_name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Batch">{item?.batch || '-'}</Descriptions.Item>
                      </Descriptions>

                      {item?.vaccine_types?.length > 0 && (
                        <>
                          <Text strong>Tipe Vaksin:</Text>
                          {item.vaccine_types.map((type, tIndex) => (
                            <Descriptions
                              key={tIndex}
                              bordered
                              size="small"
                              column={1}
                              className="mt-2 mb-2"
                              labelStyle={{ width: '20%' }}
                            >
                              <Descriptions.Item label="Nama">
                                {type?.full_name}{' '}
                                {type?.name && type?.full_name ? `- ${type.name}` : type?.name}
                              </Descriptions.Item>
                              <Descriptions.Item label="Periode">{type?.pivot?.period || '-'}</Descriptions.Item>
                              <Descriptions.Item label="Frekuensi">{type?.frequency || '-'}</Descriptions.Item>
                            </Descriptions>
                          ))}
                        </>
                      )}
                    </Card>
                  ))
                ) : (
                  <Text type="secondary">Tidak ada data vaksinasi</Text>
                )}
              </div>
            </Col>
          </Row>

        </Card>

        {/* Action Bar (Fixed Footer) */}
        <div 
          className="action-bar-footer"
          style={{ left: footerLeft - 24 }}
        >
          {/* Dropdown status antrian */}
          <div className="input-detail-queue d-flex align-items-center mb-0">
            <Text strong className="mr-3" style={{ minWidth: '120px' }}>Status Antrian :</Text>
            <Select
              style={{ width: 250 }}
              options={statusOptions}
              value={selectedStatus}
              onChange={(val) => setSelectedStatus(val)}
              placeholder="Pilih Status Antrian"
            />
          </div>

          {/* Action buttons */}
          <div className="patient-buttons m-0">
            <Link to={`/queues/${shiftId}`}>
              <Button danger size="large">Kembali</Button>
            </Link>
            
            <Button
              type="default"
              size="large"
              onClick={() => setIsNoteOpen(true)}
            >
              Catatan Anak
            </Button>
            <Button
              type="default"
              size="large"
              onClick={() => setIsVaccinOpen(true)}
            >
              Vaksinasi
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => updateStatus(selectedStatus)}
              disabled={!selectedStatus}
            >
              Simpan
            </Button>
          </div>
        </div>
      </div>

      {/* Modal: Catatan Anak */}
      <Modal
        title="Catatan Anak"
        open={isNoteOpen}
        onCancel={() => setIsNoteOpen(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setIsNoteOpen(false)}>
            OK
          </Button>,
        ]}
      >
        <p>
          {visitData?.note
            ? visitData.note
            : <Text type="danger">Belum ada catatan</Text>}
        </p>
      </Modal>

      {/* Modal: Vaksinasi */}
      <Modal
        title="Vaksinasi"
        open={isVaccinOpen}
        onCancel={() => setIsVaccinOpen(false)}
        width={600}
        footer={[
          <Button key="ok" type="primary" onClick={() => setIsVaccinOpen(false)}>
            OK
          </Button>,
        ]}
      >
        {vaccinationData?.map((item, index) => (
          <React.Fragment key={index}>
            <Descriptions bordered column={1} size="small" className="mb-3" labelStyle={{ width: '35%' }}>
              <Descriptions.Item label="Merk Vaksin">
                {item?.vaccine?.brand_name ?? <Text type="danger">Belum ada data</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Batch">
                {item?.batch ?? <Text type="danger">Belum ada data</Text>}
              </Descriptions.Item>
            </Descriptions>

            {item?.vaccine_types?.map((type, typeIndex) => (
              <Descriptions
                key={typeIndex}
                bordered
                size="small"
                column={1}
                className="mb-2"
                labelStyle={{ width: '35%' }}
              >
                <Descriptions.Item label="Tipe Vaksin">
                  {type?.full_name}{' '}
                  {type?.name && type?.full_name ? `- ${type.name}` : type?.name}
                </Descriptions.Item>
                <Descriptions.Item label="Periode">
                  {type?.pivot?.period ?? <Text type="danger">Belum ada data</Text>}
                </Descriptions.Item>
              </Descriptions>
            ))}
          </React.Fragment>
        ))}

        {!vaccinationData && (
          <Text type="danger">Belum ada data vaksinasi</Text>
        )}
      </Modal>

    </Spin>
  );
};

export default DetailActiveQueue;
